import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Upload } from "antd";
import ScrollContainer from "react-indiana-drag-scroll";
import { AppButton, FileTag } from "@/shared/Components";
import { useLazyGetFileQuery, useUploadFileMutation } from "@/services/api";
import type { FileType } from "@/shared/Components";
import type { NoteEditorFormValues, NoteEditorProps } from "@/features/NoteEditor/model";
import type { FC } from "react";
import styles from "./styles.module.scss";

type Props = {
  onFileDelete?: NoteEditorProps["onFileDelete"];
  mobile: boolean;
};

export const Files: FC<Props> = ({ onFileDelete = () => "", mobile }) => {
  const { t } = useTranslation();

  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
  const [getFile] = useLazyGetFileQuery();
  const { watch, setValue } = useFormContext<NoteEditorFormValues>();
  const onClickFile = (id: string, filename: string) => {
    getFile({ id: id })
      .unwrap()
      .then((file) => {
        if (!file) return;

        const url = window.URL.createObjectURL(file);

        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      });
  };
  const attachments = watch("currentNote.attachments");

  const handleUpload = (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    uploadFile(formData)
      .unwrap()
      .then((res) => {
        setValue("currentNote.attachments", [
          ...attachments,
          { id: null, fileDto: { type: res.type, fileId: res.fileId, fileName: res.fileName } },
        ]);
      })
      .catch((err) => console.log(err));
    return false;
  };

  return (
    <div className={styles.wrapper}>
      <ScrollContainer hideScrollbars={false} className={styles.scroll}>
        <div className={styles.root}>
          {watch("currentNote.attachments").map((file) => {
            return (
              <FileTag
                onClose={() => {
                  onFileDelete({
                    id: file.id,
                    fileDto: {
                      createdDate: file.fileDto.createdDate as string,
                      fileId: file.fileDto.fileId as string,
                      fileName: file.fileDto.fileName ?? "",
                      type: file.fileDto.type as string,
                    },
                    fileDescription: file.fileDescription ?? "",
                  });
                  setValue("currentNote.attachments", [
                    ...attachments.filter((item) => item.fileDto.fileId !== file.fileDto.fileId),
                  ]);
                }}
                onClick={() =>
                  file.fileDto.fileId &&
                  onClickFile(file.fileDto.fileId, file.fileDto.fileName ?? "")
                }
                type={(file.fileDto.fileName?.split(".")[1] as FileType) ?? "doc"}
                key={file.id}
                name={file.fileDto.fileName ?? ""}
              />
            );
          })}
          {mobile && (
            <Upload showUploadList={false} beforeUpload={handleUpload}>
              <AppButton
                className={styles["attach-file"]}
                loading={isUploading}
                disabled={isUploading}
              >
                + {t("notesPage.attachFile")}
              </AppButton>
            </Upload>
          )}
        </div>
      </ScrollContainer>
      {!mobile && (
        <Upload showUploadList={false} beforeUpload={handleUpload}>
          <AppButton className={styles["attach-file"]} loading={isUploading} disabled={isUploading}>
            + {t("notesPage.attachFile")}
          </AppButton>
        </Upload>
      )}
    </div>
  );
};
